import React, { useEffect, useMemo, useRef, useState } from 'react';
import { compose, equals, filter, includes, map, match, prop, propEq, propOr, sortBy, toLower, find, any, isNotEmpty } from 'ramda';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Select } from 'antd';
import { CloseOutlined, LayoutOutlined, LogoutOutlined, TeamOutlined, ToolOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import withUser from '../../withUser';
import { ROLES, TEAM_ROLES } from '../../lib/rights';
import SignOutMutation from '../../_graphql/mutations/SignOutMutation';
import withEnvs from '../../withEnvs';
import { onDefineActiveTeam } from '../../utils/navigation';

const menuItems = [{
  title: 'Mes offres',
  to: '/',
  isPublic: false,
  authorizedRoles: [ROLES.admin, ROLES.offerEditor, ROLES.regular, ROLES.qa]
},
{
  title: 'Mes trames',
  to: '/trames',
  isPublic: false,
  authorizedRoles: [ROLES.admin, ROLES.qa]
}];

const subMenuItems = [{
  title: 'Mes modèles',
  to: '/modeles',
  logo: <LayoutOutlined className="mr-4"/>,
  isPublic: false,
  authorizedRoles: [ROLES.admin, ROLES.offerEditor, ROLES.regular, ROLES.qa]
},
{
  title: 'Mon équipe',
  to: '/equipe',
  isPublic: false,
  logo: <TeamOutlined className="mr-4"/>,
  authorizedRoles: [ROLES.admin]
},
{
  title: 'Administration',
  to: '/administration',
  logo: <ToolOutlined className="mr-4"/>,
  authorizedRoles: [ROLES.superAdmin]
}];

const Container = ({ userTeam, teams = [], user, env = {}, userIsSuperAdmin, setIsLoading }) => {
  const history = useHistory();
  const { pathname } = useLocation();

  const appVersion = compose(
    (arr) => arr && arr[1] ? arr[1] : (arr && arr[0]) || propOr('latest', 'APP_VERSION', env),
    match(/^([0-9]+\.[0-9]+\.[0-9]+)(?:-.+)?$/),
    propOr('0.0.0', 'APP_VERSION')
  )(env);

  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(propOr('', 'teamId', userTeam));
  const [isProfilMenuOpen, setIsProfilMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => setSelectedTeam(propOr('', 'teamId', userTeam)), [userTeam]);
  useEffect(() => setSelectedLink(pathname), [pathname]);
  const currentRoles = propOr([], 'roles', userTeam);
  const userRoles = propOr([], 'roles', user);
  const userEmail = propOr('', 'email', user);

  const sortedTeams = sortBy(
    compose(
      toLower,
      prop('name')
    )
  )(teams || []);
  
  const getAvailableMenus = (items) =>
    filter((item) => {
      if (propEq(true, 'isPublic', item)) return true;
      const authorizedRoles = propOr([], 'authorizedRoles', item);

      const isAvailableForTeamRoles = any(role => includes(prop('role', role), authorizedRoles), currentRoles);
      const isAvailableForUserRoles = any(role => includes(role, authorizedRoles), userRoles);
      return isAvailableForTeamRoles || isAvailableForUserRoles;
    }, items);

  const onClose = () => setIsProfilMenuOpen(false);

  const onSignOut = () => SignOutMutation(() => history.push('/account/signin'));
  
  const role = useMemo(() => {
    if (userIsSuperAdmin) {
      return find(propEq(ROLES.superAdmin, 'value'), TEAM_ROLES).label;
    }

    return includes(ROLES.admin, map(prop('role'), currentRoles))
      ? find(propEq(ROLES.admin, 'value'), TEAM_ROLES).label
      : 'Utilisateur';
  }, [currentRoles, userIsSuperAdmin]);

  return (
    <header className="fixed top-0 left-0 w-full z-50 flex-shrink-0 h-16 bg-white flex items-center shadow-lg !p-12">
      <div className="flex justify-between w-full p-4">
        <div className="invisible md:visible inset-y-0 left-0 md:static md:flex-shrink-0 inline-flex justify-center text-center items-center">
          <Link data-testid="logo-link" to="/">
            <img
              src="/images/FASST_Flexi_Couleur.svg"
              className="w-32 cursor-pointer"
              alt="Logo Flexidocs"
            />
          </Link>
          {isNotEmpty(sortedTeams) && (
            <div className="mt-2 ml-12">
              <Select
                showSearch
                className="fd-select !text-left"
                // eslint-disable-next-line no-restricted-syntax
                style={{ width: 240 }}
                onChange={(id) => onDefineActiveTeam({
                  teamId: id,
                  user,
                  history,
                  pathname,
                  setIsLoading,
                  setSelectedTeam
                })}
                filterOption={(input, option) => includes(toLower(input), toLower(option.children))}
                notFoundContent="Aucune équipe trouvée"
                value={selectedTeam}
              >
                {map(
                  ({ id, name }) => (
                    <Select.Option key={id} value={id}>
                      {name}
                    </Select.Option>
                  ),
                  sortedTeams
                )}
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <div className="md:min-w-0 md:flex-1 md:flex md:items-center md:justify-between mt-10 md:mt-0">
            <div className="md:ml-10 pr-4 flex-shrink-0 flex items-center space-x-20">
              <nav aria-label="Global" className="flex space-x-20">
                {map(({ title, to }) => (
                  <Link
                    key={title}
                    data-testid={`${title}-link`}
                    to={to}
                    onClick={() => setSelectedLink(to)}
                    className={classNames('text-lg font-medium text-flexi-logo hover:text-flexi-primary-color', {
                      'border-b-2 border-flexi-primary-color !text-flexi-primary-color':
                      equals(selectedLink, to)
                    })}
                  >
                    {title}
                  </Link>
                ))(getAvailableMenus(menuItems))}
              </nav>
              <div className="flex items-center md:space-x-8 space-x-6">
                <div className="relative inline-block text-left">
                  <div
                    data-testid="menu-button"
                    onClick={() => setIsProfilMenuOpen(true)}
                    className="cursor-pointer bg-white flex text-sm"
                  >
                    <div className="flex-shrink-0 text-lg font-medium text-flexi-logo flex justify-center items-center !border-2 p-[.25rem] !px-4 rounded-lg">
                      <div
                        className="inline-flex items-center justify-center w-10 h-10 text-sm text-flexi-logo rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 "
                        aria-expanded="false"
                      >
                        <svg
                          className="w-5 h-5"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 17 14"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M1 1h15M1 7h15M1 13h15"
                          />
                        </svg>
                      </div>
                      Menu
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isProfilMenuOpen && (
        <nav
          className="fixed z-[100] inset-0 h-full w-full bg-white sm:inset-y-0 sm:left-auto sm:right-0 sm:max-w-sm sm:w-full sm:shadow-lg"
          aria-label="Global"
          ref={profileMenuRef}
        >
          <div className="h-[95px] flex items-center justify-between pl-4 sm:pr-[2.5rem]">
            <div className="max-w-8xl mx-auto py-8 flex items-center sm:pr-[2.5rem]">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="text-base font-medium text-gray-800 truncate">
                  {userEmail}
                </div>
                <div className="text-sm font-medium text-gray-500 truncate">
                  {role}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="-mr-2 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600"
              onClick={() => setIsProfilMenuOpen(false)}
            >
              <span className="sr-only">Fermer le menu</span>
              <CloseOutlined className="text-flexi-grey" />
            </button>
          </div>
          <div className="border-t border-gray-200 pb-3 h-[calc(100%-50px)] relative">
            <div className="flex flex-col space-y-6 p-4 text-lg !text-flexi-primary-color">
              {map(({ title, to, logo }) => (
                <Link
                  key={title} to={to} onClick={onClose}
                  className={classNames('text-flexi-logo hover:text-flexi-primary-color flex items-center font-medium', { '!text-flexi-primary-color': equals(selectedLink, to) })}
                >
                  {logo} {title}
                </Link>
              ))(getAvailableMenus(subMenuItems))}
            </div>
          </div>
          <div className="flex ml-8 absolute bottom-[3.900rem]">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <button
                  className="rounded-md py-2 px-3 font-medium text-flexi-logo hover:text-flexi-primary-color flex items-center text-lg"
                  onClick={onSignOut}
                >
                  <LogoutOutlined className="mr-4 font-medium"/> Déconnexion
                </button>
                <div>
                  <p className="text-sm text-gray-500 italic">Version {appVersion}</p>
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default withEnvs(withUser(Container));
